import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import VendorSettingLayout from './VendorSettingLayout';
import { saveVendorBillingSettings, getVendorBillingSettings, getVendorGlCodes, getVendorRoundOffGl } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
const VendorSettingBilling = () => {
    const dispatch = useDispatch();
    const { saveVendorBillingSettingsLoading: saveBillingLoading, vendorGlCodesData, vendorRoundOffGlData, vendorBillingSettingsData } = useSelector(state => state.VendorReducers);
    const { loginData } = useSelector(state => state.loginReducer);
    useEffect(() => {
        dispatch(getVendorGlCodes({ societyId: selectedSocietyStorer.getSocietyId() }));
        dispatch(getVendorRoundOffGl({ societyId: selectedSocietyStorer.getSocietyId() }));
        dispatch(getVendorBillingSettings({ societyId: selectedSocietyStorer.getSocietyId() }));
    }, [dispatch]);
    const [settings, setSettings] = useState({
        gstApplicable: true,
        defaultGstRate: 18,
        igstApplicable: false,
        defaultIgstRate: 18,
        tdsApplicable: false,
        defaultTdsRate: 10,
        tdsSection: '',
        // GST GL Mapping
        sgstGl: '',
        cgstGl: '',
        igstGl: '',
        // RCM Payable GL Mapping
        rcmSgstPayable: '',
        rcmCgstPayable: '',
        rcmIgstPayable: '',
        // RCM Receivable GL Mapping
        rcmSgstReceivable: '',
        rcmCgstReceivable: '',
        rcmIgstReceivable: '',
        // Other Settings
        roundOff: '',
        tdsGl: '',
        itemType: 'amount',
        fieldPostingDate: 'bill_date',
    });

    // Populate form when billing settings data and GL codes are loaded
    useEffect(() => {
        if (vendorBillingSettingsData && vendorGlCodesData && vendorGlCodesData.length > 0) {
            setSettings(prev => ({
                ...prev,
                gstApplicable: vendorBillingSettingsData.gst_applicable ?? prev.gstApplicable,
                defaultGstRate: vendorBillingSettingsData.default_gst_rate ?? prev.defaultGstRate,
                igstApplicable: vendorBillingSettingsData.igst_applicable ?? prev.igstApplicable,
                defaultIgstRate: vendorBillingSettingsData.default_igst_rate ?? prev.defaultIgstRate,
                tdsApplicable: Number(vendorBillingSettingsData.tds_applicable) === 1,
                defaultTdsRate: vendorBillingSettingsData.default_tds_rate ?? prev.defaultTdsRate,
                tdsSection: vendorBillingSettingsData.tds_section ?? prev.tdsSection,
                sgstGl: vendorBillingSettingsData.sgst_gl ?? prev.sgstGl,
                cgstGl: vendorBillingSettingsData.cgst_gl ?? prev.cgstGl,
                igstGl: vendorBillingSettingsData.igst_gl ?? prev.igstGl,
                rcmSgstPayable: vendorBillingSettingsData.rcm_sgst_payable ?? prev.rcmSgstPayable,
                rcmCgstPayable: vendorBillingSettingsData.rcm_cgst_payable ?? prev.rcmCgstPayable,
                rcmIgstPayable: vendorBillingSettingsData.rcm_igst_payable ?? prev.rcmIgstPayable,
                rcmSgstReceivable: vendorBillingSettingsData.rcm_sgst_receivable ?? prev.rcmSgstReceivable,
                rcmCgstReceivable: vendorBillingSettingsData.rcm_cgst_receivable ?? prev.rcmCgstReceivable,
                rcmIgstReceivable: vendorBillingSettingsData.rcm_igst_receivable ?? prev.rcmIgstReceivable,
                roundOff: vendorBillingSettingsData.roundoff_gl ?? prev.roundOff,
                tdsGl: vendorBillingSettingsData.tds_gl ?? prev.tdsGl,
                itemType: vendorBillingSettingsData.head_type?.toLowerCase() ?? prev.itemType,
                // An old approval-based value falls back to the bill date.
                fieldPostingDate: ['approval_date', 'either_of'].includes(vendorBillingSettingsData.posting_date_type)
                    ? 'bill_date'
                    : (vendorBillingSettingsData.posting_date_type ?? prev.fieldPostingDate),
            }));
        }
    }, [vendorBillingSettingsData, vendorGlCodesData]);

    const tdsSections = [
        { value: '194C', label: '194C - Payment to Contractors' },
        { value: '194J', label: '194J - Professional/Technical Fees' },
        { value: '194H', label: '194H - Commission/Brokerage' },
        { value: '194I', label: '194I - Rent' },
        { value: '194A', label: '194A - Interest (Other than Securities)' },
        { value: '194Q', label: '194Q - Purchase of Goods' },
        { value: '194M', label: '194M - Payment to Commission Agent' },
    ];

    // Bills post on their bill date. The approval-based options are left out:
    // the system has no approval step, so they have no date to post on.
    const fieldPostingDateOptions = [
        { value: 'bill_date', label: 'Bill Date' },
    ];

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const Toggle = ({ settingKey }) => (
        <button
            onClick={() => handleSettingChange(settingKey, !settings[settingKey])}
            className={`tw-w-12 tw-h-6 tw-rounded-full tw-transition-colors tw-relative tw-flex-shrink-0 ${settings[settingKey] ? 'tw-bg-blue-600' : 'tw-bg-gray-300'}`}
        >
            <span className={`tw-absolute tw-w-5 tw-h-5 tw-bg-white tw-rounded-full tw-top-0.5 tw-transition-transform ${settings[settingKey] ? 'tw-translate-x-6' : 'tw-translate-x-0.5'}`} />
        </button>
    );

    // eslint-disable-next-line no-unused-vars
    const _GlInput = ({ label, settingKey, disabled }) => (
        <div>
            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">{label}</label>
            <input
                type="text"
                value={settings[settingKey]}
                onChange={(e) => handleSettingChange(settingKey, e.target.value)}
                placeholder="Enter GL account name"
                disabled={disabled}
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400"
            />
        </div>
    );

    const GlDropdown = ({ label, settingKey, disabled }) => {
        const currentValue = settings[settingKey] || '';
        // Normalize ac_code comparison (convert last segment to 2-digit format)
        const normalizeCode = (code) => {
            if (!code) return '';
            const parts = code.split('-');
            if (parts.length > 1) {
                const lastPart = parts[parts.length - 1];
                // Convert to number and pad to 2 digits
                const num = parseInt(lastPart, 10);
                parts[parts.length - 1] = num.toString().padStart(2, '0');
                return parts.join('-');
            }
            return code;
        };
        const normalizedValue = normalizeCode(currentValue);

        return (
        <div>
            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">{label}</label>
            <select
                value={normalizedValue}
                onChange={(e) => handleSettingChange(settingKey, e.target.value)}
                disabled={disabled}
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400"
            >
                <option value="">Select GL Account</option>
                {vendorGlCodesData?.map((gl) => (
                    <option key={gl.id} value={gl.ac_code}>
                        {gl.name} ({gl.ac_code})
                    </option>
                ))}
            </select>
        </div>
        );
    };

    const RoundOffDropdown = ({ label, settingKey }) => {
        const currentValue = settings[settingKey] || '';
        // Normalize ac_code comparison (convert last segment to 2-digit format)
        const normalizeCode = (code) => {
            if (!code) return '';
            const parts = code.split('-');
            if (parts.length > 1) {
                const lastPart = parts[parts.length - 1];
                // Convert to number and pad to 2 digits
                const num = parseInt(lastPart, 10);
                parts[parts.length - 1] = num.toString().padStart(2, '0');
                return parts.join('-');
            }
            return code;
        };
        const normalizedValue = normalizeCode(currentValue);
        
        return (
        <div>
            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">{label}</label>
            <select
                value={normalizedValue}
                onChange={(e) => handleSettingChange(settingKey, e.target.value)}
                className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
            >
                <option value="">Select</option>
                {vendorRoundOffGlData?.map((gl) => (
                    <option key={gl.id} value={gl.ac_code}>
                        {gl.name} ({gl.ac_code})
                    </option>
                ))}
            </select>
        </div>
        );
    };

    const handleSave = () => {
        dispatch(saveVendorBillingSettings({
            tds_section: settings.tdsSection,
            sgst_gl: settings.sgstGl,
            cgst_gl: settings.cgstGl,
            igst_gl: settings.igstGl,
            rcm_sgst_payable: settings.rcmSgstPayable,
            rcm_cgst_payable: settings.rcmCgstPayable,
            rcm_igst_payable: settings.rcmIgstPayable,
            rcm_sgst_receivable: settings.rcmSgstReceivable,
            rcm_cgst_receivable: settings.rcmCgstReceivable,
            rcm_igst_receivable: settings.rcmIgstReceivable,
            roundoff_gl: settings.roundOff,
            tds_gl: settings.tdsGl,
            head_type: settings.itemType?.toUpperCase(),
            posting_date_type: settings.fieldPostingDate,
            societyId: selectedSocietyStorer.getSocietyId(),
            userid : loginData?.user?.user_id,
            tds_applicable: settings.tdsApplicable ? 1 : 0,
            default_tds_rate: settings.defaultTdsRate,
            
            onSuccess: () => {
                console.log('Billing settings saved successfully');
            }
        }));
    };

    return (
        <VendorSettingLayout>
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4">
                <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-4">Billing Settings</h2>
                <div className="tw-space-y-4">

                    {/* GST Settings */}
                    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-4">
                        {/* <h3 className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:file-percent" className="tw-text-green-500" />
                            GST Settings
                        </h3>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                            <div className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 tw-p-3 tw-rounded-lg">
                                <div>
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-700">GST Applicable</p>
                                    <p className="tw-text-xs tw-text-gray-500">Enable GST on vendor payments</p>
                                </div>
                                <Toggle settingKey="gstApplicable" />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Default GST Rate (%)</label>
                                <input
                                    type="number"
                                    value={settings.defaultGstRate}
                                    onChange={(e) => handleSettingChange('defaultGstRate', parseFloat(e.target.value))}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400"
                                    disabled={!settings.gstApplicable}
                                />
                            </div>
                        </div>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                            <div className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 tw-p-3 tw-rounded-lg">
                                <div>
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-700">IGST Applicable</p>
                                    <p className="tw-text-xs tw-text-gray-500">Enable IGST for inter-state transactions</p>
                                </div>
                                <Toggle settingKey="igstApplicable" />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Default IGST Rate (%)</label>
                                <input
                                    type="number"
                                    value={settings.defaultIgstRate}
                                    onChange={(e) => handleSettingChange('defaultIgstRate', parseFloat(e.target.value))}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400"
                                    disabled={!settings.igstApplicable}
                                />
                            </div>
                        </div> */}

                        {/* GST GL Mapping */}
                        <div className="tw-border-t tw-border-gray-100 tw-pt-4 tw-mt-4">
                            <h4 className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-3">GST GL Mapping</h4>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                                <GlDropdown label="SGST GL" settingKey="sgstGl" disabled={!settings.gstApplicable} />
                                <GlDropdown label="CGST GL" settingKey="cgstGl" disabled={!settings.gstApplicable} />
                                <GlDropdown label="IGST GL" settingKey="igstGl" disabled={!settings.gstApplicable} />
                            </div>
                        </div>

                        {/* RCM Payable GL Mapping */}
                        <div className="tw-border-t tw-border-gray-100 tw-pt-4 tw-mt-4">
                            <h4 className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-3">RCM Payable GL Mapping</h4>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                                <GlDropdown label="RCM SGST Payable GL" settingKey="rcmSgstPayable" disabled={!settings.gstApplicable} />
                                <GlDropdown label="RCM CGST Payable GL" settingKey="rcmCgstPayable" disabled={!settings.gstApplicable} />
                                <GlDropdown label="RCM IGST Payable GL" settingKey="rcmIgstPayable" disabled={!settings.gstApplicable} />
                            </div>
                        </div>

                        {/* RCM Receivable GL Mapping */}
                        <div className="tw-border-t tw-border-gray-100 tw-pt-4 tw-mt-4">
                            <h4 className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-3">RCM Receivable GL Mapping</h4>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                                <GlDropdown label="RCM SGST Receivable GL" settingKey="rcmSgstReceivable" disabled={!settings.gstApplicable} />
                                <GlDropdown label="RCM CGST Receivable GL" settingKey="rcmCgstReceivable" disabled={!settings.gstApplicable} />
                                <GlDropdown label="RCM IGST Receivable GL" settingKey="rcmIgstReceivable" disabled={!settings.gstApplicable} />
                            </div>
                        </div>
                    </div>

                 

                    {/* TDS Settings */}
                    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-4">
                        <h3 className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:file-percent" className="tw-text-orange-500" />
                            TDS Settings
                        </h3>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                            <div className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 tw-p-3 tw-rounded-lg">
                                <div>
                                    <p className="tw-text-sm tw-font-medium tw-text-gray-700">TDS Applicable</p>
                                    <p className="tw-text-xs tw-text-gray-500">Deduct TDS on vendor payments</p>
                                </div>
                                <Toggle settingKey="tdsApplicable" />
                            </div>
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Default TDS Rate (%)</label>
                                <input
                                    type="number"
                                    value={settings.defaultTdsRate}
                                    onChange={(e) => handleSettingChange('defaultTdsRate', parseFloat(e.target.value) || 0)}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-100"
                                    disabled={!settings.tdsApplicable}
                                />
                                <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">Used to pre-fill TDS on vendor payments</p>
                            </div>
                        </div>
                        {settings.tdsApplicable && (
                            <div className="tw-border-t tw-border-gray-100 tw-pt-4">
                                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">TDS Section</label>
                                        <select
                                            value={settings.tdsSection}
                                            onChange={(e) => handleSettingChange('tdsSection', e.target.value)}
                                            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                                        >
                                            <option value="">Select TDS Section</option>
                                            {tdsSections.map(s => (
                                                <option key={s.value} value={s.value}>{s.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Other Settings */}
                    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-p-4">
                        <h3 className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="mdi:cog" className="tw-text-gray-500" />
                            Other Settings
                        </h3>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-4">
                            <RoundOffDropdown label="Round Off" settingKey="roundOff" />
                            <GlDropdown label="TDS GL" settingKey="tdsGl" />
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Item Type</label>
                                <select
                                    value={settings.itemType}
                                    onChange={(e) => handleSettingChange('itemType', e.target.value)}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                                >
                                    <option value="unit">Unit Wise</option>
                                    <option value="amount">Amount Wise</option>
                                </select>
                            </div>
                        </div>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                            <div>
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block"> Posting Date</label>
                                <select
                                    value={settings.fieldPostingDate}
                                    onChange={(e) => handleSettingChange('fieldPostingDate', e.target.value)}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-border-blue-500"
                                >
                                    <option value="">Select</option>
                                    {fieldPostingDateOptions.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Save Button */}
            <div className="tw-flex tw-justify-end tw-mt-4">
                <button
                    onClick={handleSave}
                    disabled={saveBillingLoading}
                    className="tw-flex tw-items-center tw-gap-2 tw-bg-blue-600 tw-text-white tw-px-6 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                    {saveBillingLoading ? (
                        <>
                            <span className="tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Icon icon="mdi:content-save" />
                            Save Settings
                        </>
                    )}
                </button>
            </div>
        </VendorSettingLayout>
    );
};

export default VendorSettingBilling;
